// InstallApp — the offer to put FieldWorks on a phone's home screen.
//
// The classic app's components/PWAInstallPrompt does this. Its history is the
// design brief for this one, because both of its defects were about being
// intrusive rather than about being wrong:
//
//   - it was a `position: fixed` FAB at `zIndex: 9998`, above every dialog's
//     action buttons and above the bottom nav. That is the bug workers
//     reported twice as "buttons are missing until I turn the phone", and it
//     is why server/utils/__tests__/zIndexScale.test.js exists.
//   - on iOS it appeared three seconds after load whether or not anybody
//     asked, and its dismissal expired after seven days, so it came back.
//
// So this one is IN FLOW and never stacked. There is no `position`, no
// `z-index`, and nothing in this file or its styles can cover a control:
// occlusion is impossible by construction rather than by choosing a token
// below --z-modal. It also cannot come back on its own — see DISMISSAL below.
//
// ── What the browsers actually give us ──────────────────────────────────────
//
// Chromium fires `beforeinstallprompt` when the page is installable and not
// already installed. Calling `preventDefault()` on it keeps the browser's own
// mini-infobar off the screen and lets us hold the event, and `prompt()` on
// the held event opens the real install dialog. It fires once per page load,
// and only when the manifest (client/public/manifest.json: standalone,
// start_url /, icons) and a service worker are both in place — which is why
// there is no dead "Install" button anywhere: if the event never arrives, no
// offer is rendered.
//
// iOS Safari fires nothing and exposes no install API at all. Add to Home
// Screen is a manual sequence in the Share menu, so the only honest offer
// there is instructions. That is UA-sniffed, as the classic one does, because
// there is no capability to test for.
//
// ── DISMISSAL — the storage decision, and why ───────────────────────────────
//
// localStorage, key `fw_install_dismissed`, holding an ISO timestamp, and
// PERMANENT: nothing re-offers it. Read/write are both in try/catch because
// the accessor itself throws in a locked-down browser or private mode (the
// same reason shared/http.js wraps its token read).
//
// Why localStorage rather than the server: the question this answers is "has
// this browser, on this phone, been offered the home-screen install" — a
// property of the device, not of the person. A crew member signs in on a
// borrowed tablet and on their own phone; a server-side flag would suppress a
// real offer on the second device and would need a new column and a new
// endpoint, and server changes are out of scope here. sessionStorage is wrong
// for the opposite reason: it forgets on every app relaunch, which is exactly
// the nagging this is meant to prevent.
//
// What localStorage costs, stated rather than hidden: clearing site data
// re-offers it once, and a private window offers it every time. Both are
// acceptable for an offer that is one line and one tap to dismiss forever.
// What matters is that the STORED case is honoured on every read, which is
// what the tests pin.
//
// The `dismissible` prop is the difference between a nudge and a control:
//
//   <InstallApp dismissible />   a banner. Honours the stored dismissal, and
//                                "Not now" writes it. Shown at most once.
//   <InstallApp />               a row on a Me screen. Ignores the stored
//                                dismissal and offers no dismiss button,
//                                because the person navigated here on purpose
//                                — and it is the only way back for somebody
//                                who dismissed the banner and later wants the
//                                app installed.
//
// Both are hidden entirely once the app IS installed.

import React, { useCallback, useEffect, useState } from 'react';
import { FwButton, SectionLabel } from '../../ui/fw';

export const INSTALL_DISMISS_KEY = 'fw_install_dismissed';

/** Has this browser been told no, at any point? */
export function installDismissed() {
  try {
    return !!localStorage.getItem(INSTALL_DISMISS_KEY);
  } catch (_) {
    return false;                       // storage blocked: offer it
  }
}

/** Remember the no. Permanent — there is no expiry to reopen it. */
export function rememberInstallDismissed(at = new Date()) {
  try {
    localStorage.setItem(INSTALL_DISMISS_KEY, new Date(at).toISOString());
  } catch (_) { /* storage blocked: the offer returns next load, which is the
                   least bad outcome available when nothing can be recorded */ }
}

/** Is the app already running as an installed app rather than in a tab? */
export function isStandalone(win = typeof window === 'undefined' ? null : window) {
  if (!win) return false;
  try {
    if (win.matchMedia && win.matchMedia('(display-mode: standalone)').matches) return true;
  } catch (_) { /* matchMedia missing (jsdom) or throwing */ }
  // Safari's own, non-standard flag — the only signal iOS gives.
  return win.navigator ? win.navigator.standalone === true : false;
}

/** iOS, where there is no install API and the offer can only be instructions. */
export function isIOS(win = typeof window === 'undefined' ? null : window) {
  if (!win || !win.navigator) return false;
  return /iPad|iPhone|iPod/.test(win.navigator.userAgent || '') && !win.MSStream;
}

/**
 * What to offer, given the four facts. Pure, so every combination is a case
 * in the suite rather than a browser to find.
 *
 *   null      offer nothing — installed, dismissed, or not installable here
 *   'prompt'  a held beforeinstallprompt event: one tap opens the real dialog
 *   'ios'     instructions, the only thing iOS Safari allows
 *
 * Order matters. `standalone` wins over everything: an offer to install an app
 * that is open as an installed app is nonsense whatever else is true. Then
 * `dismissed`, so a person who said no is not asked again by a later-arriving
 * event. A held event beats iOS because it is a real install, and an iPad
 * running Chrome can produce both.
 */
export function installOffer({ standalone, dismissed, deferred, ios } = {}) {
  if (standalone) return null;
  if (dismissed) return null;
  if (deferred) return 'prompt';
  if (ios) return 'ios';
  return null;
}

/**
 * The install offer as state: what to show, and the two things to do about it.
 *
 * `dismissible: false` means the stored dismissal is not consulted — the Me
 * row always offers, so dismissing the banner is never a dead end.
 */
export function useInstallOffer({ dismissible = true } = {}) {
  const [deferred, setDeferred] = useState(null);
  // Always read the stored no; `dismissible` decides whether it COUNTS, once,
  // where mode is computed. Gating it twice (here as well) would make each
  // gate individually removable without changing behaviour, which is a guard
  // that cannot be shown to fail.
  const [dismissed, setDismissed] = useState(installDismissed);
  const [standalone, setStandalone] = useState(() => isStandalone());
  const [ios] = useState(() => isIOS());

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onBeforeInstall = (e) => {
      // Keeps Chromium's own mini-infobar off the screen and lets us hold the
      // event; without this the browser shows its bar AND we show ours.
      if (e && e.preventDefault) e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    // Installed from anywhere — our button, the browser menu, another tab.
    const onInstalled = () => { setStandalone(true); setDeferred(null); };
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const dismiss = useCallback(() => {
    rememberInstallDismissed();
    setDismissed(true);
  }, []);

  const install = useCallback(async () => {
    if (!deferred || !deferred.prompt) return;
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice && choice.outcome === 'accepted') setStandalone(true);
    } catch (_) {
      // A prompt() the browser refuses (already consumed, wrong gesture) is
      // not something to put in front of a crew member. The offer stays.
    }
    // The event is single-use whatever the person chose: a second prompt() on
    // it throws. Dropping it also removes the offer, which is right — the
    // browser has had its say for this page load.
    setDeferred(null);
  }, [deferred]);

  return {
    mode: installOffer({ standalone, dismissed: dismissible ? dismissed : false, deferred, ios }),
    install,
    dismiss,
    standalone,
  };
}

const IOS_STEPS = [
  'Tap the Share button in Safari — the square with an arrow.',
  'Scroll down and tap "Add to Home Screen".',
  'Tap "Add". The icon appears on your home screen.',
];

/**
 * @param {boolean} [dismissible] banner (true) or Me row (false) — see the
 *   header. Default false: a mount that has not thought about it gets the
 *   variant that cannot nag.
 */
export default function InstallApp({ dismissible = false, title = 'Install this app' }) {
  const { mode, install, dismiss } = useInstallOffer({ dismissible });
  const [showSteps, setShowSteps] = useState(false);

  if (!mode) return null;

  return (
    <section className="fw-install" aria-label={title}>
      <SectionLabel>{title}</SectionLabel>
      <p className="fw-install__why">
        Add FieldWorks to your home screen and it opens like an app — full screen, and it
        still works where there is no signal.
      </p>

      {mode === 'prompt' ? (
        <div className="fw-install__actions">
          <FwButton variant="primary" icon="download" onClick={install}>Install</FwButton>
          {dismissible ? <FwButton variant="ghost" onClick={dismiss}>Not now</FwButton> : null}
        </div>
      ) : null}

      {mode === 'ios' ? (
        <>
          <div className="fw-install__actions">
            <FwButton
              variant="ghost"
              icon="download"
              onClick={() => setShowSteps((v) => !v)}
              aria-expanded={showSteps}
            >
              {showSteps ? 'Hide the steps' : 'How to install'}
            </FwButton>
            {dismissible ? <FwButton variant="ghost" onClick={dismiss}>Not now</FwButton> : null}
          </div>
          {showSteps ? (
            <ol className="fw-install__steps">
              {IOS_STEPS.map((step) => <li key={step}>{step}</li>)}
            </ol>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
